import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import MutateGate from '../../../components/Common/MutateGate';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { useSalesPointOfSale } from '../../../contexts/SalesPointOfSaleContext';
import { salesOrderService } from '../../../services/salesOrderService';
import {
  downloadVentasBulkImportTemplate,
  parseVentasBulkImportExcel,
} from '../../../utils/ventasBulkImportExcel';
import {
  getVentasPaymentMethodLabel,
  VENTAS_PAYMENT_METHODS,
} from '../../../utils/ventasSalesConstants';

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatMoney(amount) {
  return `S/ ${parseFloat(amount || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const VentasBulkImport = () => {
  const canMutate = useCanMutate();
  const navigate = useNavigate();
  const { selectedPointOfSale } = useSalesPointOfSale();
  const [issueDate, setIssueDate] = useState(todayLocal());
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ has_otro_series: true, shift_closed: false, max_orders: 2000 });
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fileName, setFileName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const posId = selectedPointOfSale?.id || null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await salesOrderService.getFormData(posId);
        if (!cancelled && res.success) {
          setClients(Array.isArray(res.data?.clients) ? res.data.clients : []);
        }
      } catch {
        if (!cancelled) setClients([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [posId]);

  useEffect(() => {
    if (!posId) {
      setProducts([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingMeta(true);
        const res = await salesOrderService.getBulkImportTemplate({
          point_of_sale_id: posId,
          issue_date: issueDate || undefined,
        });
        if (cancelled) return;
        if (res.success) {
          setProducts(Array.isArray(res.data?.products) ? res.data.products : []);
          setMeta({
            has_otro_series: Boolean(res.data?.has_otro_series),
            shift_closed: Boolean(res.data?.shift_closed),
            max_orders: res.data?.max_orders || 2000,
          });
        } else {
          setProducts([]);
          setToast({ isOpen: true, message: res.message || 'No se pudo cargar el catálogo del POS', type: 'error' });
        }
      } catch (e) {
        if (!cancelled) {
          setProducts([]);
          setToast({
            isOpen: true,
            message: e.response?.data?.message || 'Error al cargar plantilla',
            type: 'error',
          });
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [posId, issueDate]);

  const canSubmit = useMemo(() => {
    if (!canMutate || !posId || !clientId || !issueDate) return false;
    if (!meta.has_otro_series || meta.shift_closed) return false;
    if (!parsedRows.length || parseErrors.length) return false;
    if (summary && summary.total_units > meta.max_orders) return false;
    return true;
  }, [canMutate, posId, clientId, issueDate, meta, parsedRows, parseErrors, summary]);

  const handleDownloadTemplate = async () => {
    if (!products.length) {
      setToast({ isOpen: true, message: 'No hay productos habilitados con precio en este POS.', type: 'error' });
      return;
    }
    const code = selectedPointOfSale?.code || 'pos';
    await downloadVentasBulkImportTemplate(products, `venta-masiva-${code}-${issueDate || todayLocal()}.xlsx`);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileName(file.name);
    setParseErrors([]);
    setParsedRows([]);
    setSummary(null);
    try {
      const result = await parseVentasBulkImportExcel(file, products);
      setParseErrors(result.errors || []);
      setParsedRows(result.rows || []);
      setSummary(result.summary);
      if ((result.errors || []).length && !(result.rows || []).length) {
        setToast({ isOpen: true, message: 'El archivo tiene errores. Revise el detalle.', type: 'error' });
      }
    } catch {
      setToast({ isOpen: true, message: 'No se pudo leer el Excel.', type: 'error' });
    }
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      const res = await salesOrderService.bulkImport({
        point_of_sale_id: posId,
        client_id: Number(clientId),
        issue_date: issueDate,
        rows: parsedRows,
      });
      if (res.success) {
        navigate('/ventas/orders', {
          state: {
            orderSavedMessage: res.message || `Se registraron ${res.data?.created_count || 0} ventas.`,
            dateFrom: issueDate,
            dateTo: issueDate,
          },
        });
        return;
      }
      setToast({ isOpen: true, message: res.message || 'No se pudo importar', type: 'error' });
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al importar ventas masivas',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!posId) {
    return (
      <div className="p-6">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-6 text-center text-gray-600">
          Seleccione un punto de venta para importar ventas masivas.{' '}
          <Link to="/ventas/select-point-of-sale" state={{ from: '/ventas/orders/bulk-import' }} className="text-teal-700 font-medium">
            Elegir POS
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Importar venta masiva</h1>
            <p className="text-gray-600 mt-1 max-w-2xl">
              Documento <strong>Otros</strong>, sin IGV. Cada unidad genera una venta; el stock se mueve al cerrar el turno.
            </p>
            <p className="text-sm text-teal-700 mt-2">
              POS:{' '}
              <strong>
                {selectedPointOfSale.code ? `${selectedPointOfSale.code} — ` : ''}
                {selectedPointOfSale.name}
              </strong>
            </p>
          </div>
          <Link
            to="/ventas/orders"
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium"
          >
            Volver a ventas
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-5 mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del día *</label>
              <input
                type="date"
                value={issueDate}
                onChange={(e) => {
                  setIssueDate(e.target.value);
                  setParsedRows([]);
                  setSummary(null);
                  setParseErrors([]);
                  setFileName('');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              {meta.shift_closed && (
                <p className="mt-1 text-sm text-red-600">El turno de esta fecha ya está cerrado.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Seleccionar cliente…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code ? `${c.code} — ` : ''}
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!meta.has_otro_series && (
            <p className="text-sm text-red-600">
              Configure la serie de documento <strong>Otros</strong> en Ventas → Serie por punto de venta.
            </p>
          )}

          <div className="flex flex-wrap gap-3 items-center">
            <MutateGate>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                disabled={loadingMeta || !products.length}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 text-sm font-medium"
              >
                Descargar plantilla ({products.length} productos)
              </button>
            </MutateGate>
            <MutateGate>
              <label className="px-4 py-2 border border-teal-600 text-teal-700 rounded-md hover:bg-teal-50 text-sm font-medium cursor-pointer">
                Subir Excel
                <input type="file" accept=".xlsx" className="hidden" onChange={handleFileChange} disabled={loadingMeta || meta.shift_closed} />
              </label>
            </MutateGate>
            {fileName ? <span className="text-sm text-gray-500">{fileName}</span> : null}
          </div>
        </div>

        {parseErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h2 className="font-semibold text-red-800 mb-2">Errores de validación</h2>
            <ul className="text-sm text-red-700 list-disc pl-5 space-y-1 max-h-40 overflow-y-auto">
              {parseErrors.slice(0, 40).map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
            {parseErrors.length > 40 && (
              <p className="text-xs text-red-600 mt-2">…y {parseErrors.length - 40} más</p>
            )}
          </div>
        )}

        {summary && (
          <div className="bg-white rounded-lg shadow-md p-5 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Previsualización</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-gray-50 rounded-md p-3">
                <div className="text-xs text-gray-500">Productos con venta</div>
                <div className="text-xl font-semibold">{summary.product_rows}</div>
              </div>
              <div className="bg-gray-50 rounded-md p-3">
                <div className="text-xs text-gray-500">Ventas a crear</div>
                <div className="text-xl font-semibold">{summary.total_units}</div>
              </div>
              <div className="bg-gray-50 rounded-md p-3 col-span-2">
                <div className="text-xs text-gray-500">Monto total</div>
                <div className="text-xl font-semibold">{formatMoney(summary.total_amount)}</div>
              </div>
            </div>
            {summary.total_units > meta.max_orders && (
              <p className="text-sm text-red-600 mb-3">
                Supera el máximo de {meta.max_orders} ventas por archivo.
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="py-2 pr-4">Medio de pago</th>
                    <th className="py-2 pr-4 text-right">Cantidad</th>
                    <th className="py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {VENTAS_PAYMENT_METHODS.map((m) => {
                    const row = summary.by_payment?.[m.value] || { count: 0, amount: 0 };
                    if (!row.count) return null;
                    return (
                      <tr key={m.value} className="border-b border-gray-100">
                        <td className="py-2 pr-4">{getVentasPaymentMethodLabel(m.value)}</td>
                        <td className="py-2 pr-4 text-right">{row.count}</td>
                        <td className="py-2 text-right">{formatMoney(row.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex justify-end">
              <MutateGate>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canSubmit || submitting}
                  className="px-5 py-2.5 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 font-medium"
                >
                  {submitting ? 'Importando…' : `Confirmar e importar ${summary.total_units} ventas`}
                </button>
              </MutateGate>
            </div>
          </div>
        )}
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default VentasBulkImport;
